// src/components/SearchResults.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import ProductCard from './ProductCard';
import { useAfiliado } from '../context/AfiliadoContext';
import { buscarProductosInteligente, normalizar } from '../utils/searchHelper';

// Términos especiales que no buscan texto sino un campo booleano
const TERMINO_RECOMENDADOS = 'recomendados';

const SUGERENCIAS_POPULARES = [
  'Colágeno',
  'Magnesio',
  'Proteína',
  'Omega 3',
  'Energía',
  'Higado siempre sano',
];

const detectarAvisoBusqueda = (queryTexto, productos) => {
  if (!queryTexto || productos.length === 0) return null;
  const qNorm = normalizar(queryTexto);

  // Perfiles de usuario
  if (['ninos', 'nino', 'nina', 'kids', 'infantil'].includes(qNorm)) {
    return 'Mostrando productos certificados para Niños e Infantil';
  }
  if (['mujer', 'mujeres', 'femenino', 'women'].includes(qNorm)) {
    return 'Mostrando productos para Mujeres y Bienestar Femenino';
  }
  if (['hombre', 'hombres', 'masculino', 'men'].includes(qNorm)) {
    return 'Mostrando productos para Hombres y Salud Masculina';
  }
  if (['adultos mayores', 'adulto mayor', 'abuelo', 'abuelos', 'tercera edad', 'senior'].includes(qNorm)) {
    return 'Mostrando productos para Adultos Mayores';
  }

  if (qNorm === 'collagen' || qNorm === 'colajeno') {
    return 'Coincidencia inteligente: Colágeno';
  }
  if (qNorm === 'protein') {
    return 'Coincidencia inteligente: Proteínas';
  }
  if (qNorm === 'magnesium' || qNorm === 'magnecio') {
    return 'Coincidencia inteligente: Magnesio';
  }
  if (qNorm === 'sleep') {
    return 'Coincidencia inteligente: Descanso y sueño';
  }
  if (qNorm === 'liver') {
    return 'Coincidencia inteligente: Salud hepática y detox';
  }

  // Si no coincide literalmente en el primer nombre pero sí mediante búsqueda inteligente
  const primerNombre = normalizar(productos[0]?.nombre || '');
  if (!primerNombre.includes(qNorm) && qNorm.length >= 4) {
    return `Coincidencia inteligente para "${queryTexto}"`;
  }

  return null;
};

const SearchResults = ({ queryTexto, addToCart, setSearchQuery }) => {
  const { afiliado, vistaAfiliado } = useAfiliado();
  const esAfiliado = Boolean(afiliado && vistaAfiliado);

  const [productos, setProductos] = useState([]);
  const [loading, setLoading]     = useState(true);

  // Subir el scroll al entrar
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [queryTexto]);

  useEffect(() => {
    const fetchBusqueda = async () => {
      if (!queryTexto) return;

      try {
        setLoading(true);
        const productosRef = collection(db, 'catalogo');
        const esRecomendados = queryTexto.trim().toLowerCase() === TERMINO_RECOMENDADOS;

        // ── Caso especial: "Recomendados" ──
        if (esRecomendados) {
          const q = query(
            productosRef,
            where('recomendado', '==', true),
            where('visible', '==', true)
          );
          const snap = await getDocs(q);
          const docs = [];
          snap.forEach((doc) => docs.push({ id: doc.id, ...doc.data() }));
          setProductos(docs);
          return;
        }

        // ── Búsqueda inteligente (sinónimos, inglés/español y errores tipográficos) ──
        const q = query(productosRef, where('visible', '!=', false));
        const querySnapshot = await getDocs(q);

        let todosLosProductos = [];
        querySnapshot.forEach((doc) => {
          todosLosProductos.push({ id: doc.id, ...doc.data() });
        });

        const resultadosFiltrados = buscarProductosInteligente(todosLosProductos, queryTexto);
        setProductos(resultadosFiltrados);
      } catch (error) {
        console.error('Error buscando:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBusqueda();
  }, [queryTexto]);

  const avisoInteligente = useMemo(() => {
    return detectarAvisoBusqueda(queryTexto, productos);
  }, [queryTexto, productos]);

  // ── Skeleton de carga ──
  if (loading) {
    return (
      <div style={{
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        padding: 'var(--space-5) var(--space-3) var(--space-8)',
        boxSizing: 'border-box',
        fontFamily: 'var(--font-body)',
      }}>
        <div className="grid-container" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 'var(--space-3)',
          justifyContent: 'center',
        }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: '100%', maxWidth: '180px' }}>
                <div className="skeleton-pulse" style={{ width: '100%', height: '150px', borderRadius: 'var(--radius-lg)' }} />
                <div className="skeleton-pulse" style={{ width: '80%', height: '12px', borderRadius: 'var(--radius-sm)', margin: '10px auto 6px' }} />
                <div className="skeleton-pulse" style={{ width: '50%', height: '12px', borderRadius: 'var(--radius-sm)', margin: '0 auto' }} />
              </div>
            </div>
          ))}
        </div>
        <style>{`
          .skeleton-pulse {
            background: linear-gradient(90deg, #f0f0ee 25%, #fff 50%, #f0f0ee 75%);
            background-size: 200% 100%;
            animation: skeleton-loading 1.4s ease-in-out infinite;
          }
          @keyframes skeleton-loading {
            0%   { background-position:  200% 0; }
            100% { background-position: -200% 0; }
          }
          @media (min-width: 768px) {
            .grid-container { grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)) !important; }
          }
        `}</style>
      </div>
    );
  }

  // ── Resultados ──
  return (
    <div style={{
      width: '100%',
      maxWidth: '1200px',
      margin: '0 auto',
      padding: 'var(--space-3) var(--space-3) var(--space-8)',
      boxSizing: 'border-box',
      fontFamily: 'var(--font-body)',
      minHeight: '100vh',
    }}>

      {/* Aviso de coincidencia inteligente (ej. collagen -> colágeno) */}
      {avisoInteligente && (
        <div style={{
          backgroundColor: 'var(--color-verde-tint, #f0fdf4)',
          border: '1px solid var(--color-verde-border, #90c98a)',
          color: 'var(--color-verde, #16a34a)',
          borderRadius: 'var(--radius-md, 8px)',
          padding: '8px 14px',
          marginBottom: 'var(--space-4)',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          maxWidth: '600px',
          margin: '0 auto var(--space-4)',
        }}>
          <span>💡</span>
          <span>{avisoInteligente}</span>
        </div>
      )}

      {/* Indicador de descuento de afiliado */}
      {esAfiliado && productos.length > 0 && (
        <div style={{
          backgroundColor: 'var(--color-verde-tint, #f0fdf4)',
          border: '1px solid var(--color-verde, #16a34a)',
          color: 'var(--color-verde, #16a34a)',
          borderRadius: 'var(--radius-md, 8px)',
          padding: '8px 14px',
          marginBottom: 'var(--space-4)',
          fontSize: 'var(--font-size-xs)',
          fontWeight: 600,
          textAlign: 'center',
          maxWidth: '600px',
          margin: '0 auto var(--space-4)',
        }}>
          🏷️ Mostrando precios de afiliado (25% OFF aplicado)
        </div>
      )}

      <div
        className="grid-container"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 'var(--space-6) var(--space-3)',
          width: '100%',
          justifyContent: 'center',
          justifyItems: 'center',
        }}
      >
        {productos.length > 0 ? (
          productos.map((prod) => (
            <div key={prod.id} style={{
              width: '100%',
              maxWidth: '180px',
              display: 'flex',
              justifyContent: 'center',
            }}>
              <div style={{ width: '100%', display: 'flex' }}>
                <ProductCard 
                  producto={prod} 
                  addToCart={addToCart}
                  esAfiliado={esAfiliado}
                  vistaAfiliado={vistaAfiliado}
                  afiliado={afiliado}
                />
              </div>
            </div>
          ))
        ) : (
          /* Estado vacío con sugerencias inteligentes integradas */
          <div style={{
            gridColumn: '1 / -1',
            textAlign: 'center',
            padding: 'var(--space-8) var(--space-5)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--space-3)',
          }}>
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none"
              stroke="var(--color-gris-bd)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <p style={{
              color: 'var(--text-secondary)',
              fontSize: 'var(--font-size-body)',
              margin: 0,
              fontWeight: 'var(--font-weight-medium)',
            }}>
              No encontramos coincidencias para "{queryTexto}"
            </p>
            <p style={{
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-small)',
              fontWeight: 'var(--font-weight-light)',
              margin: 0,
              maxWidth: '320px',
            }}>
              Prueba buscando por una de nuestras categorías o términos populares:
            </p>

            {setSearchQuery && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                justifyContent: 'center',
                marginTop: 'var(--space-2)',
                maxWidth: '420px',
              }}>
                {SUGERENCIAS_POPULARES.map((sug) => (
                  <button
                    key={sug}
                    type="button"
                    onClick={() => setSearchQuery(sug)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 'var(--radius-pill)',
                      border: '1px solid var(--border-default)',
                      backgroundColor: 'var(--color-gris-fondo)',
                      color: 'var(--color-verde)',
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all var(--transition-fast)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-verde-tint)';
                      e.currentTarget.style.borderColor = 'var(--color-verde)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-gris-fondo)';
                      e.currentTarget.style.borderColor = 'var(--border-default)';
                    }}
                  >
                    🔍 {sug}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @media (min-width: 768px) {
          .grid-container {
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)) !important;
            max-width: 1000px;
            margin: 0 auto;
            gap: var(--space-6) var(--space-5) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SearchResults;
